import { Button, CircularProgress, makeStyles, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography } from '@material-ui/core';
import { InstitutionContext } from '@ngt/online-patient-management-ui';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Field, FormRenderProps, useField } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useHistory } from 'react-router-dom';
import client from '../../api/client';
import { ILookup, SaeMedicalReview, SaeMedicalReviewItem, SaeMedicalReviewPostSave, SaeMedicalReviewSingleByPatientStudyNumber } from '../../api/Neon.Dtos';
import { GetFieldLookup } from '../../context/LookupsContext';
import { useSaeById } from '../../store/modules/saereview/sae';
import AsyncForm from '../FinalForm/AsyncForm';
import { CheckboxWrapper } from '../FinalForm/FinalFormControls/CheckboxWrapper';
import { SelectWrapper } from '../FinalForm/FinalFormControls/SelectWrapper';
import TextFieldWrapper from '../FinalForm/FinalFormControls/TextFieldWrapper';

import * as Yup from 'yup';

import { grey } from '@material-ui/core/colors';
import arrayMutators from 'final-form-arrays';


interface ISaeMedicalReviewFormProps {
    patientStudyNumber?: string,
    saeNumber?: string;
    viewMode?: string;
}

interface ISaeMedicalReviewFormInternalProps extends FormRenderProps<SaeMedicalReview> {
    patientStudyNumber?: string,
    saeNumber?: string;
    viewMode?: string;
    onFormSave?: () => void;
}

const relatednessPropertyPath = 'lastDoseStudy.drugRelatedEvent'
const medicalReviewRelatednessPropertyPath = 'MedicalReview.Relatedness'
const expectednessPropertyPath = 'MedicalReview.Expectedness'
const treatmentPropertyPath = 'lastDoseStudy.drugName'

const useSaeMedicalReviewForm = (patientStudyNumber: string, saeNumber: number) => {
    const [form, setForm] = React.useState<SaeMedicalReview | null>(null);
    const [formLoading, setFormLoading] = React.useState(true);
    const [formError, setFormError] = React.useState(false);

    React.useEffect(() => {
        const getForm = async () => {
            setFormLoading(true);
            setFormError(false);

            try {
                const response = await client.get(new SaeMedicalReviewSingleByPatientStudyNumber({
                    patientStudyNumber,
                    saeNumber
                }));

                setForm(response.saeMedicalReview);
            }
            catch {
                setFormError(true);
            }

            setFormLoading(false);
        }

        getForm();
    }, [patientStudyNumber, saeNumber]);

    return {
        form,
        formLoading,
        formError
    }
}

const useInitialStyles = makeStyles<Theme>(theme => ({
    loading: {
        textAlign: 'center',
        padding: theme.spacing(3)
    },
    section: {
        margin: theme.spacing(3, 0)
    }
}));

const submitMedicalReview = async (saeMedicalReview: SaeMedicalReview) => {
    return await client.post(new SaeMedicalReviewPostSave({ saeMedicalReview }))
};

const SaeMedicalReviewForm: React.FunctionComponent<ISaeMedicalReviewFormProps> = ({
    saeNumber,
    patientStudyNumber,
    viewMode
}) => {
    const classes = useInitialStyles();

    const history = useHistory();

    const [institution] = React.useContext(InstitutionContext);

    const [, , , ] = useSaeById(
        patientStudyNumber!,
        +saeNumber!
    );

    const { form, formLoading, formError } = useSaeMedicalReviewForm(patientStudyNumber!, +saeNumber!);



    const formSubscriptionParams = {
        submitting: true,
        pristine: false,
        submitError: true,
        dirtySinceLastSubmit: true,
        hasValidationErrors: true,
        valid: true,
        validating: true,
        errors: true,
        form: true
    };

    const validation = Yup.object().shape<SaeMedicalReview>({
        id: Yup.number(),
        date: Yup.string(),
        patientStudyNumber: Yup.string(),
        saeFollowUpNumber: Yup.number(),
        saeNumber: Yup.number(),
        saeStatus: Yup.number(),
        items: Yup.array().min(0, '').of(Yup.object().shape<SaeMedicalReviewItem>({
            id: Yup.number(),
            dataEntered: Yup.boolean(),
            expected: Yup.number()
                .when(['notApplicable', 'relatednessId'], (notApplicable: boolean, relatednessId: number, schema: Yup.NumberSchema) => !notApplicable && relatednessId && relatednessId > 1 ? schema.required('Required!') : schema),
            latestRelatednessId: Yup.number(),
            notApplicable: Yup.boolean(),
            relatednessId: Yup.number()
                .when('notApplicable', (notApplicable: boolean, schema: Yup.NumberSchema) => notApplicable ? schema : schema.required('Required!')),
            saeMedicalReviewId: Yup.number(),
            saeTreatmentCategoryId: Yup.number(),
            typeId: Yup.number(),
            comments: Yup.string()
        })),
        narrativeSummary: Yup.string()
    });

    const { enqueueSnackbar } = useSnackbar();

    const onMedicalReviewFormSave = React.useCallback(() => {

        enqueueSnackbar('Medical review has been successfully submitted.', { variant: 'success' });

        history.push(`/sae/${institution?.code}/${patientStudyNumber}/${saeNumber}`);
    }, [institution?.code, patientStudyNumber, saeNumber, enqueueSnackbar, history]);

    const medicalReviewForm = React.useCallback((props: FormRenderProps<SaeMedicalReview>) => {
        return (
            <SaeMedicalReviewFormInternal
                patientStudyNumber={patientStudyNumber}
                saeNumber={saeNumber}
                viewMode={viewMode}
                onFormSave={onMedicalReviewFormSave}
                {...props}
            />
        );
    }, [onMedicalReviewFormSave, patientStudyNumber, saeNumber, viewMode]);

    return (
        <>
            {
                formLoading && (
                    <div className={classes.loading}>
                        <CircularProgress />
                        <div>
                            <Typography variant="subtitle1">
                                Loading Medical Review
                            </Typography>
                        </div>
                    </div>
                )
            }
            {
                !formLoading && !formError && (
                    <div className={classes.section}>
                        <Typography component={'span'}>
                            An SAE has been updated for the Neo-N trial.
                            <br />
                            <br />
                            <b>Please submit a medical review using the form below:</b>
                            <AsyncForm
                                initialValues={form}
                                validate={ validation }
                                onSubmit={submitMedicalReview}
                                formSubscription={formSubscriptionParams}
                                validationDebounceTimeout={200}
                                component={medicalReviewForm}
                                mutators={{
                                    ...arrayMutators
                                }}
                            />
                        </Typography>
                    </div>
                )
            }
        </>
    );
};

const useFormStyles = makeStyles<Theme>(theme => ({
    static: {
        background: grey[200]
    },
    buttonSet: {
        textAlign: 'right',
        marginTop: theme.spacing(2)
    },
    narrativeSummary: {
        marginTop: theme.spacing(2)   
    }
}));

const SaeMedicalReviewFormInternal: React.FunctionComponent<ISaeMedicalReviewFormInternalProps> = ({
    onFormSave,
    saeNumber,
    patientStudyNumber,
    viewMode,
    ...formRenderProps
}) => {

    const classes = useFormStyles();

    const [sae, , , ] = useSaeById(
        patientStudyNumber!,
        +saeNumber!
    );

    const treatmentLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, treatmentPropertyPath);
    }, [sae]);

    const relatednessLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, relatednessPropertyPath);
    }, [sae]);

    const expectednessLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, expectednessPropertyPath);
    }, [sae]);

    const medicalReviewRelatednessLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, medicalReviewRelatednessPropertyPath);
    }, [sae]);

    const { enqueueSnackbar } = useSnackbar();

    const onMedicalReviewFormSubmit = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to submit the review.', { variant: 'error' });
        }
    }, [enqueueSnackbar, formRenderProps, onFormSave]);

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        {
                            <TableCell className={classes.static} style={{ 'width': '20%' }}>
                                <Typography>
                                    <strong>
                                        Study Drug
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                        {
                            <TableCell className={classes.static}  style={{ 'width': '15%' }}>
                                <Typography>
                                    <strong>
                                        Site Investigator Relatedness Assessment
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                        {
                            <TableCell className={classes.static}  style={{ 'width': '10%' }}>
                                <Typography>
                                    <strong>
                                        Not Applicable
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                        {
                            <TableCell className={classes.static}  style={{ 'width': '15%' }}>
                                <Typography>
                                    <strong>
                                        Relatedness Assessment
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                        {
                            <TableCell className={classes.static} style={{ 'width': '15%' }}>
                                <Typography>
                                    <strong>
                                        Expectedness
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                        {
                            <TableCell className={classes.static} style={{ 'width': '25%' }}>
                                <Typography>
                                    <strong>
                                        Comments/Clinical Query
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    <FieldArray name="items">
                        {({ fields }) => {
                            return fields?.map((field, i) => {
                                return (
                                    <SaeMedicalReviewFormItem
                                        key={i}
                                        field={field}
                                        treatmentLookup={treatmentLookup}
                                        relatednessLookup={relatednessLookup}
                                        expectednessLookup={expectednessLookup}
                                        medicalReviewRelatednessLookup={medicalReviewRelatednessLookup}
                                    />
                                );
                            })
                        }}
                    </FieldArray>
                </TableBody>
            </Table>
            <div className={classes.narrativeSummary}>
                <Typography>
                    Narrative Summary
                </Typography>
                <Field
                    fullWidth
                    name={`narrativeSummary`}
                    type="text"
                    component={TextFieldWrapper}
                    multiline={true}
                    rows={3}
                    variant="outlined"
                />
            </div>
            <Typography>
                <br />
                <u>For Australian Participants please note:</u>
                <br />
                <br />
                <b>TGA Blue Card Notification:</b>
                <br />
                <br />
                        If a SAE has ‘Relatedness’ of “Possible”, “Probable” and “Definite” and ‘Expectedness’ of “Unexpected”, TGA notification within 15 days of BCT’s knowledge of the event is required.
                        <br />
                <br />
                <b>TGA Death Notification:</b>
                <br />
                <br />
                In case of SAE (with ‘Relatedness’ of “Possible”, “Probable” and “Definite”) resulting in ‘Death’, regardless of ‘Expectedness’, TGA notification within 7 days of BCT’s knowledge of the event is required.
            </Typography>
            <div className={classes.buttonSet}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onMedicalReviewFormSubmit}
                >
                    Submit Review
                </Button>
            </div>
        </>
    )
};

interface ISaeMedicalReviewFormItemProps {
    field: string;
    treatmentLookup: ILookup | null;
    expectednessLookup: ILookup | null;
    relatednessLookup: ILookup | null;
    medicalReviewRelatednessLookup: ILookup | null;
}

const useStyles = makeStyles<Theme, { notApplicable: boolean; related: boolean; }>(theme => ({
    static: {
        background: grey[50]
    },
    disabled: {
        background: ({ notApplicable }) => notApplicable ? grey[50] : 'transparent'
    },
    relatedDisabled: {
        background: ({ notApplicable, related }) => notApplicable || !related ? grey[50] : 'transparent'
    }
}));

const SaeMedicalReviewFormItem: React.FunctionComponent<ISaeMedicalReviewFormItemProps> = ({
    field,
    expectednessLookup,
    relatednessLookup,
    treatmentLookup,
    medicalReviewRelatednessLookup
}) => {
    const { input: { value: notApplicable } } = useField<boolean>(`${field}.notApplicable`, { subscription: { value: true } });
    const { input: { value: relatednessId } } = useField<number>(`${field}.relatednessId`, { subscription: { value: true } });

    const related = !!relatednessId && relatednessId > 1;

    const classes = useStyles({ notApplicable, related })

    return (
        <TableRow>
            <TableCell className={classes.static}>
                <Field
                    fullWidth
                    name={`${field}.typeId`}
                    type="text"
                    label="Study drug"
                >
                    {props => (
                        <Typography>
                            {treatmentLookup?.items?.find(li => li.id === props.input.value)?.value}
                        </Typography>
                    )}
                </Field>
            </TableCell>
            <TableCell className={classes.static}>
                <Field
                    fullWidth
                    name={`${field}.latestRelatednessId`}
                    type="text"
                    label="Investigator assessment"
                >
                    {props => (
                        <Typography>
                            {relatednessLookup?.items?.find(li => li.id === props.input.value)?.value ?? 'Not Provided'}
                        </Typography>
                    )}
                </Field>
            </TableCell>
            <TableCell>
                <Field
                    name={`${field}.notApplicable`}
                    component={CheckboxWrapper}
                    label="Not Applicable"
                />
            </TableCell>
            <TableCell className={classes.disabled}>
                <Field
                    name={`${field}.relatednessId`}
                    component={SelectWrapper}
                    type="text"
                    formControlProps={{
                        fullWidth: true
                    }}
                    disabled={notApplicable}
                >
                    <MenuItem value="">&nbsp;</MenuItem>
                    {
                        medicalReviewRelatednessLookup?.items?.map(li => {
                            return (
                                <MenuItem key={li.id} value={li.id}> {li.value} </MenuItem>
                            );
                        })
                    }
                </Field>
            </TableCell>
            <TableCell className={classes.relatedDisabled}>
                <Field
                    name={`${field}.expected`}
                    component={SelectWrapper}
                    type="text"
                    formControlProps={{
                        fullWidth: true
                    }}
                    disabled={notApplicable || !related}
                >
                    <MenuItem value="">&nbsp;</MenuItem>
                    {
                        expectednessLookup?.items?.map(li => {
                            return (
                                <MenuItem key={li.id} value={li.id}> {li.value} </MenuItem>
                            );
                        })
                    }
                </Field>
            </TableCell>
            <TableCell className={classes.disabled}>
                <Field
                    fullWidth
                    name={`${field}.comments`}
                    component={TextFieldWrapper}
                    type="text"
                    multiline={true}
                    rows={3}
                    variant="outlined"
                    disabled={notApplicable}
                />
            </TableCell>
        </TableRow>
    );
};

export default SaeMedicalReviewForm;